import React, { useState } from 'react';
import Calendar10 from '@/components/calendar-10';
import { Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { BookingConfig, TimeSlot } from '../../types/booking';

interface DatePickerProps {
  value: string;
  timeValue: string;
  timeSlots: TimeSlot[];
  onChange: (date: string) => void;
  onTimeSelect: (time: string) => void;
  config: BookingConfig;
  error?: string;
  timeError?: string;
}

export const DatePicker: React.FC<DatePickerProps> = ({ 
  value, 
  timeValue,
  timeSlots,
  onChange, 
  onTimeSelect,
  config, 
  error,
  timeError
}) => {
  const { theme, darkMode } = config;
  const [currentPage, setCurrentPage] = useState(0);
  const slotsPerPage = 7; // 7 slots with bigger buttons to fill container

  const getMinDate = () => {
    const now = new Date();
    const minDate = new Date(now.getTime() + config.minAdvanceHours * 60 * 60 * 1000);
    return minDate;
  };

  const getMaxDate = () => {
    const now = new Date();
    const maxDate = new Date(now.getTime() + config.maxAdvanceDays * 24 * 60 * 60 * 1000);
    return maxDate;
  };

  const isDateDisabled = (date: Date) => {
    // Get day of week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
    const dayOfWeek = date.getDay();
    
    // Disable weekends (Saturday = 6, Sunday = 0)
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      return true;
    }
    
    // For date comparison, we need to compare dates only (not datetime)
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const minDateOnly = new Date();
    minDateOnly.setHours(0, 0, 0, 0); // Start of today
    const maxDateOnly = new Date();
    maxDateOnly.setDate(maxDateOnly.getDate() + config.maxAdvanceDays);
    maxDateOnly.setHours(23, 59, 59, 999); // End of max day
    
    // Check if date is before today or after maximum advance days
    if (dateOnly < minDateOnly || dateOnly > maxDateOnly) {
      return true;
    }
    
    // Check blackout dates
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    
    return config.blackoutDates.includes(dateString);
  };

  const selectedDate = value ? new Date(value + 'T00:00:00') : undefined;

  // Pagination logic for time slots
  const totalPages = Math.ceil(timeSlots.length / slotsPerPage);
  const startIndex = currentPage * slotsPerPage;
  const endIndex = startIndex + slotsPerPage;
  const currentSlots = timeSlots.slice(startIndex, endIndex);

  const goToNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Reset page when date changes
  React.useEffect(() => {
    setCurrentPage(0);
  }, [value]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calendar on the left */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {config.localization.translations['datePicker.selectDate'] || 'Selecteer een datum'}
          </label>
          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg h-[390px] overflow-hidden">
            <Calendar10 
              selected={selectedDate}
              onSelect={(date) => {
                if (date) {
                  // Format date properly without timezone conversion issues
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  onChange(`${year}-${month}-${day}`);
                }
              }}
              disabled={isDateDisabled}
            />
          </div>
          {error && (
            <p 
              className="text-sm text-red-500 flex items-center gap-1"
              role="alert"
              aria-live="polite"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {error}
            </p>
          )}
        </div>

        {/* Time slots on the right - SAME HEIGHT AS CALENDAR CONTAINER */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {config.localization.translations['datePicker.selectTime'] || 'Selecteer een tijd'}
          </label>
          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-4 h-[390px] flex flex-col">
            {!value ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <Clock className="w-8 h-8 mx-auto mb-2 opacity-50 text-gray-500" />
                <p className="text-gray-500 text-sm">{config.localization.translations['datePicker.selectDateFirst'] || 'Selecteer eerst een datum'}</p>
              </div>
            ) : timeSlots.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <Clock className="w-8 h-8 mx-auto mb-2 opacity-50 text-gray-500" />
                <p className="text-gray-500 text-sm">{config.localization.translations['datePicker.noAvailableTime'] || 'Geen beschikbare tijden voor deze datum'}</p>
              </div>
            ) : (
              <div className="flex flex-col h-full">
                {/* Time slots - VERTICAL LAYOUT */}
                <div className="flex-1 py-1">
                  <div className="space-y-1">
                    {currentSlots.map((slot) => (
                      <button
                        key={slot.time}
                        type="button"
                        disabled={!slot.available}
                        onClick={() => onTimeSelect(slot.time)}
                        className={`w-full p-2.5 rounded-lg text-sm font-medium transition-all duration-200 focus:outline-none ${
                          timeValue === slot.time
                            ? 'shadow-md border-0'
                            : slot.available
                            ? `hover:shadow-sm border ${
            darkMode
                                  ? 'bg-gray-800 border-gray-600 text-gray-200 hover:bg-gray-700'
                                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                              }`
                            : 'opacity-50 cursor-not-allowed bg-gray-700 border-gray-600 text-gray-500 border'
          }`}
          style={{
                          backgroundColor: timeValue === slot.time ? theme.primary : undefined,
                          borderColor: timeValue === slot.time ? 'transparent' : (darkMode ? 'rgba(75, 85, 99, 1)' : 'rgba(209, 213, 219, 1)'),
                          color: timeValue === slot.time ? 'white' : undefined,
          } as React.CSSProperties}
                        aria-pressed={timeValue === slot.time}
                        aria-disabled={!slot.available}
                      >
                        {slot.time}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Navigation arrows and pagination info */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between pt-2 border-t border-white/20">
                    <button
                      type="button"
                      onClick={goToPrevPage}
                      disabled={currentPage === 0}
                      className={`p-1 rounded-lg transition-all duration-200 ${
                        currentPage === 0
                          ? 'opacity-50 cursor-not-allowed text-gray-500'
                          : 'hover:bg-white/10 text-gray-300 hover:text-white'
                      }`}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    
                    <span className="text-xs text-gray-400">
                      {(config.localization.translations['datePicker.pageInfo'] || '{current} van {total}')
                        .replace('{current}', String(currentPage + 1))
                        .replace('{total}', String(totalPages))
                      }
                    </span>
                    
                    <button
                      type="button"
                      onClick={goToNextPage}
                      disabled={currentPage === totalPages - 1}
                      className={`p-1 rounded-lg transition-all duration-200 ${
                        currentPage === totalPages - 1
                          ? 'opacity-50 cursor-not-allowed text-gray-500'
                          : 'hover:bg-white/10 text-gray-300 hover:text-white'
                      }`}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          {timeError && (
            <p 
              className="text-sm text-red-500 flex items-center gap-1"
              role="alert"
              aria-live="polite"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {timeError}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};